/**
 * Incremental SHA-256 over a browser File/Blob without loading it all into memory.
 * SubtleCrypto only supports one-shot digests, so this is a compact software hasher.
 */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

function rotr(n, x) {
  return (x >>> n) | (x << (32 - n));
}

function ch(x, y, z) {
  return (x & y) ^ (~x & z);
}

function maj(x, y, z) {
  return (x & y) ^ (x & z) ^ (y & z);
}

function sigma0(x) {
  return rotr(2, x) ^ rotr(13, x) ^ rotr(22, x);
}

function sigma1(x) {
  return rotr(6, x) ^ rotr(11, x) ^ rotr(25, x);
}

function gamma0(x) {
  return rotr(7, x) ^ rotr(18, x) ^ (x >>> 3);
}

function gamma1(x) {
  return rotr(17, x) ^ rotr(19, x) ^ (x >>> 10);
}

class Sha256 {
  constructor() {
    this.h = new Uint32Array([
      0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
    ]);
    this.buffer = new Uint8Array(64);
    this.bufLen = 0;
    this.bytes = 0;
  }

  update(chunk) {
    const data = chunk instanceof Uint8Array ? chunk : new Uint8Array(chunk);
    let offset = 0;
    this.bytes += data.length;

    if (this.bufLen > 0) {
      const need = 64 - this.bufLen;
      const take = Math.min(need, data.length);
      this.buffer.set(data.subarray(0, take), this.bufLen);
      this.bufLen += take;
      offset = take;
      if (this.bufLen === 64) {
        this._compress(this.buffer);
        this.bufLen = 0;
      }
    }

    while (offset + 64 <= data.length) {
      this._compress(data.subarray(offset, offset + 64));
      offset += 64;
    }

    if (offset < data.length) {
      this.buffer.set(data.subarray(offset), 0);
      this.bufLen = data.length - offset;
    }
    return this;
  }

    digestHex() {
    // 64-bit bit-length (big-endian). Number is exact for slide sizes (<< 2^53).
    const totalBits = this.bytes * 8;
    const bitLenHi = Math.floor(totalBits / 4294967296);
    const bitLenLo = totalBits % 4294967296;

    // padding
    this.buffer[this.bufLen++] = 0x80;
    if (this.bufLen > 56) {
      while (this.bufLen < 64) this.buffer[this.bufLen++] = 0;
      this._compress(this.buffer);
      this.bufLen = 0;
    }
    while (this.bufLen < 56) this.buffer[this.bufLen++] = 0;

    this.buffer[56] = (bitLenHi >>> 24) & 0xff;
    this.buffer[57] = (bitLenHi >>> 16) & 0xff;
    this.buffer[58] = (bitLenHi >>> 8) & 0xff;
    this.buffer[59] = bitLenHi & 0xff;
    this.buffer[60] = (bitLenLo >>> 24) & 0xff;
    this.buffer[61] = (bitLenLo >>> 16) & 0xff;
    this.buffer[62] = (bitLenLo >>> 8) & 0xff;
    this.buffer[63] = bitLenLo & 0xff;
    this._compress(this.buffer);

    let hex = "";
    for (let i = 0; i < 8; i += 1) {
      hex += (this.h[i] >>> 0).toString(16).padStart(8, "0");
    }
    return hex;
  }

  _compress(block) {
    const w = new Uint32Array(64);
    for (let i = 0; i < 16; i += 1) {
      const j = i * 4;
      w[i] =
        ((block[j] << 24) | (block[j + 1] << 16) | (block[j + 2] << 8) | block[j + 3]) >>> 0;
    }
    for (let i = 16; i < 64; i += 1) {
      w[i] = (gamma1(w[i - 2]) + w[i - 7] + gamma0(w[i - 15]) + w[i - 16]) >>> 0;
    }

    let a = this.h[0];
    let b = this.h[1];
    let c = this.h[2];
    let d = this.h[3];
    let e = this.h[4];
    let f = this.h[5];
    let g = this.h[6];
    let hh = this.h[7];

    for (let i = 0; i < 64; i += 1) {
      const t1 = (hh + sigma1(e) + ch(e, f, g) + K[i] + w[i]) >>> 0;
      const t2 = (sigma0(a) + maj(a, b, c)) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = (d + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }

    this.h[0] = (this.h[0] + a) >>> 0;
    this.h[1] = (this.h[1] + b) >>> 0;
    this.h[2] = (this.h[2] + c) >>> 0;
    this.h[3] = (this.h[3] + d) >>> 0;
    this.h[4] = (this.h[4] + e) >>> 0;
    this.h[5] = (this.h[5] + f) >>> 0;
    this.h[6] = (this.h[6] + g) >>> 0;
    this.h[7] = (this.h[7] + hh) >>> 0;
  }
}

const DEFAULT_CHUNK = 4 * 1024 * 1024; // 4 MiB

/**
 * @param {Blob} file
 * @param {{ chunkSize?: number, signal?: AbortSignal }} [opts]
 * @returns {Promise<string>} lowercase hex SHA-256
 */
export async function sha256HexOfBlob(file, opts = {}) {
  const chunkSize = opts.chunkSize || DEFAULT_CHUNK;
  const signal = opts.signal;
  const hasher = new Sha256();
  let offset = 0;
  while (offset < file.size) {
    if (signal?.aborted) {
      const err = new Error("Aborted");
      err.name = "AbortError";
      throw err;
    }
    const end = Math.min(offset + chunkSize, file.size);
    const buf = await file.slice(offset, end).arrayBuffer();
    hasher.update(new Uint8Array(buf));
    offset = end;
    // Yield so UI stays responsive on huge WSIs
    await new Promise((r) => setTimeout(r, 0));
  }
  return hasher.digestHex();
}

/**
 * @param {Blob} file
 * @param {number} prefixLen
 * @param {{ signal?: AbortSignal }} [opts]
 */
export async function sha256PrefixOfBlob(file, prefixLen, opts = {}) {
  const hex = await sha256HexOfBlob(file, opts);
  return hex.slice(0, prefixLen);
}
